import { Colors } from '@oy/uicore';
import React from 'react';
import ReactSelect, { GroupBase, MenuPlacement, Props } from 'react-select';
import { colorToRgb } from '../../../../utilities/color';
import { formatString, makeId } from '../../../../utilities/idComponent';
import { getMessageIconUrl } from '../../../../utilities/inputMessage';
import {
  FieldContainer,
  FieldCustomMessage,
  FontStyle,
} from '../../FieldContainer';

export type SelectOption = {
  value: string;
  label: React.ReactNode;
};

export type SelectProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = {
  boldLabel?: boolean;
  customMessage?: FieldCustomMessage | null;
  /**
   * Will be passed to react-select as `styles` props
   * @deprecated Use `styles` instead
   */
  customStyleProps?: any;
  /**
   * Will be passed to react-select as `inputId` props
   * @deprecated Use `inputId` instead
   */
  id?: string;
  /**
   * Will be passed to react-select as `menuPlacement` props
   * @deprecated Use `menuPlacement` instead
   */
  placement?: MenuPlacement;
  errorMessage?: string;
  errorMessageId?: string;
  helpMessage?: React.ReactNode;
  helpText?: string;
  idParent?: string;
  isHorizontal?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  labelStyle?: FontStyle;
  leftColLength?: number;
  requiredSign?: boolean;
  rightChild?: React.ReactNode;
  shouldUseMessageColor?: boolean;
  subLabel?: string;
  warningMessage?: string;
  onClickHelpText?: () => void;
} & Props<Option, IsMulti, Group>;

const Select = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  boldLabel,
  customMessage,
  customStyleProps,
  errorMessage,
  helpMessage,
  helpText,
  id,
  isRequired,
  label,
  leftColLength,
  name,
  rightChild,
  shouldUseMessageColor,
  subLabel,
  onClickHelpText,
  errorMessageId = '',
  formatOptionLabel = undefined,
  idParent = '',
  isHorizontal = false,
  labelStyle = FontStyle.default,
  noOptionsMessage = () => 'No Options',
  menuPortalTarget = document.body,
  placement = 'auto',
  warningMessage,
  ...props
}: SelectProps<Option, IsMulti, Group>) => {
  const getBorderColor = () => {
    if (errorMessage) {
      return Colors.RED100;
    } else if (warningMessage) {
      return Colors.YELLOW100;
    } else if (customMessage && customMessage.color) {
      return customMessage.color;
    }
    return Colors.BLACK20;
  };

  const getBoxShadow = () => {
    const borderColor = getBorderColor();
    const rgb = colorToRgb(borderColor);
    if (borderColor !== Colors.BLACK20 && rgb) {
      return `0 0 0 0.2rem rgb(${rgb.r} ${rgb.g} ${rgb.b} / 25%)`;
    }
    return '0 0 0 0.2rem rgb(13 71 161 / 25%)';
  };

  const getMessageIconStyles = () => {
    const styles: Record<string, any> = {};
    let icon = '';
    if (customMessage && customMessage.icon) {
      icon = getMessageIconUrl(customMessage.icon, customMessage.color);
    }
    if (icon) {
      styles['padding-right'] = 'var(--token-spacing-l)';
      styles['background-image'] = `url("${icon}")`;
      styles['background-repeat'] = 'no-repeat';
      styles['background-position'] = 'right center';
      styles['background-size'] = '18px 18px';
    }
    return styles;
  };

  const customStyles: Props<Option, IsMulti, Group>['styles'] = {
    control: (base, state) => ({
      ...base,
      borderColor: getBorderColor(),
      boxShadow: state.isFocused ? getBoxShadow() : 'none',
      '&:hover': {
        borderColor: getBorderColor(),
      },
    }),
    placeholder: base => ({
      ...base,
      ...getMessageIconStyles(),
    }),
    menu: base => ({ ...base, zIndex: 8 }),
    option: base => ({
      ...base,
      fontSize: 14,
    }),
    menuPortal: base => ({ ...base, zIndex: 1001 }),
    ...customStyleProps,
    ...props.styles,
  };

  const defaultOptionLabel: Props<
    Option,
    IsMulti,
    Group
  >['formatOptionLabel'] = data => (
    <div id={makeId('list', formatString((data as SelectOption).label))}>
      {(data as SelectOption).label}
    </div>
  );

  return (
    <FieldContainer
      id={makeId('listSelect', formatString(name ?? label), idParent)}
      name={name}
      customMessage={customMessage}
      errorMessage={errorMessage}
      errorMessageId={errorMessageId}
      fontStyle={labelStyle}
      helpText={helpText}
      isHorizontal={isHorizontal}
      isRequired={isRequired}
      label={label}
      leftColLength={leftColLength}
      onClickHelpText={onClickHelpText}
      rightChild={rightChild}
      shouldUseMessageColor={shouldUseMessageColor}
      subLabel={subLabel}
      warningMessage={warningMessage}
      helpMessage={helpMessage}
      boldLabel={boldLabel}
    >
      <ReactSelect
        inputId={id}
        menuPlacement={placement}
        {...props}
        menuPortalTarget={menuPortalTarget}
        styles={customStyles}
        formatOptionLabel={formatOptionLabel ?? defaultOptionLabel}
        noOptionsMessage={noOptionsMessage}
        className={['font-body-MdRegular', props.className].join(' ')}
      />
    </FieldContainer>
  );
};

export default Select;
